import React, { useEffect, useState } from 'react'
import { useQuery } from '@apollo/client'
import { makeStyles } from '@material-ui/core/styles'
import { CircularProgress } from '@material-ui/core'
import GraphQlErrors from 'src/components/common/GraphQlErrors'
import {
  GET_INSURANCE_PROVIDER_DATA,
  GET_MASTER_DATA,
  GET_TPA_DATA,
  GET_TRACKED_DETAILS_FOR_POLICIES,
} from 'src/services/individualService'
import { POLICY_STATUS_TYPES } from 'src/utils/constants/common'
import { formattedDate, formatTime } from 'src/utils/commonMethods/formatDate'
import TrackFormFields from 'src/components/common/RenewPolicy/TrackFormFields'

const TrackRenewedPolicyForm = ({
  policyData,
  closeModal,
  isTracking,
  trackClick,
  clientType,
}) => {
  const classes = useStyles()
  const [tpaListData, setTpaListData] = useState([])
  const [insuranceProviderListData, setInsuranceProviderListData] = useState([])
  const policyId = policyData?.id
  const [forceFetch, setForceFetch] = useState(false)

  const {
    loading: trackedDetailsLoading,
    error: trackedDetailsError,
    data: trackedDetailsData,
    refetch: refetchTrackedDetails,
  } = useQuery(GET_TRACKED_DETAILS_FOR_POLICIES(policyId), {
    errorPolicy: 'all',
    fetchPolicy: 'catch-and-network',
  })

  const {
    loading: tpaLoading,
    error: tpaError,
    data: tpaData,
  } = useQuery(GET_TPA_DATA(), {
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  })
  const {
    loading: insuranceProviderLoading,
    error: insuranceProviderError,
    data: insuranceProviderData,
  } = useQuery(GET_INSURANCE_PROVIDER_DATA(), {
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  })

  const {
    loading: masterDataLoading,
    error: masterDataError,
    data: masterData,
  } = useQuery(GET_MASTER_DATA(), {
    errorPolicy: 'all',
  })

  useEffect(() => {
    if (tpaData) {
      const parsedTPA = tpaData?.getMasterData?.response?.[0]?.data?.map(
        (item) => JSON.parse(item)
      )
      setTpaListData(parsedTPA)
    }
  }, [tpaData])

  useEffect(() => {
    if (insuranceProviderData) {
      const parsedInsuranceProvider =
        insuranceProviderData?.getMasterData?.response?.[0]?.data?.map((item) =>
          JSON.parse(item)
        )
      setInsuranceProviderListData(parsedInsuranceProvider)
    }
  }, [insuranceProviderData])

  useEffect(() => {
    setForceFetch()
  }, [forceFetch])

  useEffect(() => {
    if (policyId) {
      refetchTrackedDetails()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policyId])

  if (trackedDetailsLoading) return <CircularProgress />
  if (trackedDetailsError)
    return <GraphQlErrors error={trackedDetailsError} routeValue={'back'} />

  if (
    trackedDetailsLoading ||
    masterDataLoading ||
    tpaLoading ||
    insuranceProviderLoading
  )
    return (
      <div>
        <CircularProgress />
      </div>
    )

  if (
    trackedDetailsError ||
    masterDataError ||
    tpaError ||
    insuranceProviderError
  )
    return (
      <>
        <GraphQlErrors
          error={
            trackedDetailsError ||
            masterDataError ||
            tpaError ||
            insuranceProviderError
          }
          routeValue={'back'}
        />
      </>
    )

  const trackedDetails =
    trackedDetailsData?.getTrackedDetailsForPolicy?.policyDetails

  const insuranceTypesData = masterData?.getInsuranceTypeData
  const oldPolicyId = trackedDetails?.oldPolicyId

  return (
    <div className={classes.mainTrackContainer}>
      {isTracking && (
        <div className={classes.mainContainer}>
          <div className={classes.container}>
            <span className={classes.headingText}>Renewed by</span>
          </div>
          <div className={classes.detailsContainer}>
            <div className={classes.container}>
              <span className={classes.detailsContent}>
                Employee ID: {trackedDetails?.renewedBy?.id}
              </span>
            </div>
            <div className={classes.container}>
              <span className={classes.detailsContent}>
                Name: {trackedDetails?.renewedBy?.firstName}&nbsp;
                {trackedDetails?.renewedBy?.lastName}
              </span>
            </div>
            <div className={classes.container}>
              <span className={classes.detailsContent}>
                Date: {formattedDate(trackedDetails?.renewalOn)}
              </span>
            </div>
            <div className={classes.container}>
              <span className={classes.detailsContent}>
                Time: {formatTime(trackedDetails?.renewalOn)}
              </span>
            </div>
          </div>
          {trackedDetails?.status === POLICY_STATUS_TYPES?.active &&
            oldPolicyId && (
              <div className={classes.previousPolicy}>
                <span>Renewed Policy Id: {trackedDetails?.id}</span>
              </div>
            )}
          {trackedDetails?.status === POLICY_STATUS_TYPES?.renewed && (
            <div className={classes.previousPolicy}>
              <span>Previous Policy Id: {trackedDetails?.id}</span>
            </div>
          )}
        </div>
      )}

      <TrackFormFields
        expiredPolicyData={policyData}
        tpaListData={tpaListData}
        insuranceProviderData={insuranceProviderListData}
        closeModal={closeModal}
        isTracking={isTracking}
        oldPolicyId={oldPolicyId}
        insuranceTypesData={insuranceTypesData}
        trackedDetails={trackedDetails}
        policyId={policyId}
        trackClick={trackClick}
        clientType={clientType}
        trackedDetailsLoading={trackedDetailsLoading}
        trackedDetailsError={trackedDetailsError}
      />
    </div>
  )
}

export default TrackRenewedPolicyForm
const useStyles = makeStyles({
  mainRenewContainer: {
    textAlign: 'center',
    padding: '10px',
  },
  errorText: {
    fontSize: '13px',
    fontweight: '400',
    color: '#B92250',
    fontStyle: 'Roboto',
  },
  container: {
    color: '#3F9972',
    fontSize: '14px',
    fontFamily: 'roboto',
    fontWeight: '400',
    lineHeight: '25px',
  },
  mainContainer: {
    textAlign: 'center',
    padding: '10px',
  },
  detailsContainer: {
    display: 'flex',
    justifyContent: 'center',
    textAlign: 'center',
    gap: '3%',
  },
  previousPolicy: {
    fontSize: '14px',
    fontFamily: 'roboto',
    lineHeight: '25px',
    fontweight: '400',
    color: '#D80027',
    textAlign: 'center',
    paddingTop: '20px',
  },
  mainTrackContainer: {
    overflow: 'auto',
    height: '500px',
    margin: '30px 0px 0px 0px',
    padding: '0px 20px',
    background: 'linear-gradient(180deg, #FFFFFF 67.38%, #C69DAA 132.78%)',
  },
})
